// src/modules/accounting/pages/BranchExpenseReportPage.jsx
// Cumulative expense spend per branch across a chosen period.
// Attribution is driven purely by CompanyExpense.branch, so bank-paid HO expenses
// (rent, electricity) and cash petty-cash spends roll up into the same branch total.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Row, Col, Card, Statistic, DatePicker, Button, Tag, Space,
  message, Progress, Empty
} from 'antd';
import { ReloadOutlined, ShopOutlined, WalletOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getExpenseBranchSummary } from '../api';
import { EXPENSE_CATEGORY_MAP } from '../constants';
import ManualExpenseEntryModal from '../components/ManualExpenseEntryModal';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const catLabel = (c) => EXPENSE_CATEGORY_MAP[c]?.label || (c || '').replace(/_/g, ' ');

export default function BranchExpenseReportPage() {
  const [range, setRange] = useState([dayjs().startOf('year'), dayjs()]);
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range?.[0]) params.startDate = range[0].startOf('day').toISOString();
      if (range?.[1]) params.endDate = range[1].endOf('day').toISOString();
      const res = await getExpenseBranchSummary(params);
      setRows(res.data?.branches || []);
      setGrandTotal(res.data?.grandTotal || 0);
    } catch {
      messageApi.error('Failed to load branch expense report');
    } finally {
      setLoading(false);
    }
  }, [range, messageApi]);

  useEffect(() => { load(); }, [load]);

  const topBranch = rows[0];

  const columns = [
    {
      title: 'Branch', dataIndex: 'branchName', key: 'branch',
      render: (v, r) => (
        <Space>
          <Tag color={r.branchId ? 'geekblue' : 'default'}>{v}</Tag>
          {r.branchCode && <Text type="secondary" style={{ fontSize: 12 }}>{r.branchCode}</Text>}
        </Space>
      ),
    },
    { title: 'Entries', dataIndex: 'count', key: 'count', width: 100, align: 'right' },
    {
      title: 'Total Spend', dataIndex: 'totalAmount', key: 'total', width: 160, align: 'right',
      sorter: (a, b) => a.totalAmount - b.totalAmount, defaultSortOrder: 'descend',
      render: (v) => <Text strong style={{ color: '#cf1322' }}>{fmt(v)}</Text>,
    },
    {
      title: 'Share', key: 'share', width: 180,
      render: (_, r) => {
        const pct = grandTotal ? Math.round((r.totalAmount / grandTotal) * 100) : 0;
        return <Progress percent={pct} size="small" strokeColor="#1890ff" />;
      },
    },
  ];

  const expandedRowRender = (record) => {
    const cats = [...(record.categories || [])].sort((a, b) => b.totalAmount - a.totalAmount);
    return (
      <DataTable persistId="accounting.branch-expenses.detail" showToolbar={false}
        rowKey={(c) => c.category}
        dataSource={cats}
        pagination={false}
        size="small"
        columns={[
          { title: 'Category', dataIndex: 'category', render: (c) => <Tag>{catLabel(c)}</Tag> },
          { title: 'Entries', dataIndex: 'count', width: 100, align: 'right' },
          { title: 'Amount', dataIndex: 'totalAmount', width: 160, align: 'right', render: (v) => <Text>{fmt(v)}</Text> },
        ]}
      />
    );
  };

  return (
    <div>
      {contextHolder}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            <ShopOutlined style={{ marginRight: 8 }} />
            Branch-wise Expense Report
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Cumulative spend per branch — cash (drawer) and bank (HO) expenses combined.
          </Text>
        </Col>
        <Col>
          <Space wrap>
            <RangePicker
              value={range && range.length === 2 ? range : null}
              onChange={(v) => setRange(v || [])}
              format="DD/MM/YYYY"
              allowClear
            />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEntryOpen(true)}>
              Manual Entry
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic
              title={<span><WalletOutlined /> Total Expense</span>}
              value={fmt(grandTotal)}
              valueStyle={{ fontSize: 20, fontWeight: 600, color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic title="Branches" value={rows.filter((r) => r.branchId).length} valueStyle={{ fontSize: 20 }} />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic
              title="Top Spender"
              value={topBranch ? topBranch.branchName : '—'}
              valueStyle={{ fontSize: 20 }}
            />
            {topBranch && <Text type="secondary" style={{ fontSize: 11 }}>{fmt(topBranch.totalAmount)}</Text>}
          </Card>
        </Col>
      </Row>

      <DataTable persistId="accounting.branch-expenses.main"
        rowKey={(r) => r.branchId || 'unassigned'}
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        expandable={{ expandedRowRender }}
        pagination={false}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No expenses in this period" /> }}
      />

      <ManualExpenseEntryModal
        open={entryOpen}
        onClose={() => setEntryOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
